<template>
  <div>
    <div :class="{ nomalMode: modeNormal, headerMode: modeHeader, active: canShowHeader }" class="searchConditions">
      <template v-if="!pageDetector.isSimilarEventsPage && !pageDetector.isSimilarEventGroupPage">
        <div v-show="side === 'form'" class="searchConditions-content selectForm">
          <template v-if="!$store.isSP">
            <div class="optionItemGroup">
              <div :class="{ active: isSelectedPurpose }" class="optionItem condition btn-select-tag" @click="openPurposeModal">
                <span class="optionItem-value">
                  目的<span v-if="isSelectedPurpose" class="optionItem-value-count">({{ selectedPurposeCount }})</span>
                </span>
              </div>
              <div :class="{ active: model.prefecture_slug }" class="optionItem condition btn-select-tag" @click="modal = 'prefecture'">
                <span class="optionItem-value">
                  {{ selectedPrefectureName }}
                </span>
              </div>
              <div :class="{ active: model.areas.length > 0 }" class="optionItem condition btn-select-tag" @click="modal = 'area'">
                <span class="optionItem-value">{{ selectedAreaName || 'エリア' }}</span>
              </div>
              <div :class="{ active: isCheckedConditions }" class="optionItem condition btn-select-tag" @click="openModalSearch">
                <span class="optionItem-value">
                  詳細条件<span v-if="checkedConditionsCount > 0" class="optionItem-value-count">({{ checkedConditionsCount }})</span>
                </span>
              </div>
              <div :class="dateClasses" class="optionItem condition btn-select-tag" @click="modal = 'calendar'">
                <span class="optionItem-value selectedDate">
                  {{ selectedDateFormatHoliday === '開催日を選ぶ' ? '開催日' : selectedDateFormatHoliday || '開催日' }}
                </span>
              </div>
            </div>
          </template>

          <!-- SPの場合のみscrollable-boxを使う -->
          <scrollable-box v-else :disable-animation="true">
            <div class="optionItemGroup">
              <div :class="{ active: isSelectedPurpose }" class="optionItem condition btn-select-tag" @click="openPurposeModal">
                <span class="optionItem-value">
                  目的<span v-if="isSelectedPurpose" class="optionItem-value-count">({{ selectedPurposeCount }})</span>
                </span>
              </div>
              <div :class="{ active: model.prefecture_slug }" class="optionItem condition btn-select-tag" @click="modal = 'prefecture'">
                <span class="optionItem-value">
                  {{ selectedPrefectureName }}
                </span>
              </div>
              <div :class="{ active: model.areas.length > 0 }" class="optionItem condition btn-select-tag" @click="modal = 'area'">
                <span class="optionItem-value">
                  {{ selectedAreaName || 'エリア' }}
                </span>
              </div>
              <div :class="{ active: isCheckedConditions }" class="optionItem condition btn-select-tag" @click="openModalSearch">
                <span class="optionItem-value">
                  詳細条件<span v-if="checkedConditionsCount > 0" class="optionItem-value-count">({{ checkedConditionsCount }})</span>
                </span>
              </div>
              <div :class="dateClasses" class="optionItem condition btn-select-tag" @click="modal = 'calendar'">
                <span class="optionItem-value selectedDate">
                  {{ selectedDateFormatHoliday === '開催日を選ぶ' ? '開催日' : selectedDateFormatHoliday || '開催日' }}
                </span>
              </div>
            </div>
          </scrollable-box>
        </div>
      </template>
    </div>
    <simple-search-modal
      v-if="modal === 'purpose' || modal === 'calendar'"
      :modal="modal"
      :modal-data="modalData"
      :position="currentYPosition"
      :parent-model="model"
      @close-modal="modal = ''"
      @apply-purposes="applyPurposes"
      @apply-feature="applyFeature"
      @apply-date="applyDate"
    />
    <location-search-modal
      v-if="modal === 'prefecture' || modal === 'area'"
      :modal="modal"
      :modal-data="modalData"
      :position="currentYPosition"
      :parent-model="model"
      @close-modal="modal = ''"
      @apply-location="applyLocation"
    />
    <sp-search v-if="modalSearch" :position="currentYPosition" @close-modal="closeModalSearch" />
  </div>
</template>

<script>
import EventModel from '@/models/events'
import { findPrefectureBySlug } from '@/models/prefectures'
import GlobalCookie from '@/utils/GlobalCookie'
import PageDetector from '@/utils/PageDetector'
import { defaultTimeSearchCondition as defaultTime } from '@/utils/Time'
import { flattenArray, uniqArray } from '@/utils/array'
import SearchFormMixin from '@/utils/mixins/SearchDetailForm'
import SimpleSearchMixin from '@/utils/mixins/SimpleSearch'
import SearchParams from '@/utils/search/SearchParams'

const SpSearch = defineAsyncComponent(() => import('@/components/search/SpSearch.vue'))
const SimpleSearchModal = defineAsyncComponent(() => import('@/components/modals/SimpleSearchModal.vue'))
const LocationSearchModal = defineAsyncComponent(() => import('@/components/search/LocationSearchModal.vue'))
const ScrollableBox = defineAsyncComponent(() => import('@/components/ScrollableBox.vue'))
const SHOW_SEARCH_HEADER_DISTANCE = 800
const SHOW_SEARCH_HEADER_DISTANCE_ON_KONKATSU_PAGE = 2000
const SHOW_SEARCH_HEADER_DISTANCE_ON_INTEREST_PAGE = 1200
const CONTENT_EVENT_TYPES = {
  thoroughly: ['machicon', 'petit-con'],
  many_people: ['machicon', 'party'],
  konkatsu: 'konkatsu'
}

export default {
  components: { SimpleSearchModal, SpSearch, ScrollableBox, LocationSearchModal },
  mixins: [SimpleSearchMixin, SearchFormMixin],
  props: {
    mode: { type: String, default: 'normal', required: false },
    showSearchForm: { type: Boolean, default: false, required: false }
  },
  setup() {
    const isModalOpen = ref(false)

    useHeadSafe({
      bodyAttrs: {
        class: { openModal: isModalOpen }
      }
    })

    return {
      isModalOpen
    }
  },
  data() {
    return {
      prefectures: [],
      side: 'form',
      autoSubmitSearch: true,
      needRemoveQueries: {
        prefecture_slug: ['prefecture_slug', 'prefecture'],
        region: ['prefecture[]', 'region'],
        purpose: ['cat_in[]'],
        syumiplusPurposes: ['feature[]'],
        interestPurposes: ['interest[]'],
        areas: ['area', 'area[]'],
        onlineEventTypes: ['online_event_type[]'],
        start_date: ['from-day', 'from-month', 'from-year'],
        end_date: ['to-day', 'to-month', 'to-year'],
        start_dates: ['start_dates[]'],
        from_time: 'from-time',
        features: ['feature'],
        have_interest: 'have-interest'
      },
      modalSearch: false,
      currentYPosition: 0
    }
  },
  computed: {
    defaultTime,
    pageDetector() {
      return new PageDetector(this.$route)
    },
    searchParams() {
      return new SearchParams(this.$store, this.$route).apiSearchParams
    },
    searchConditions() {
      // search conditions that returned from api
      const conditions = this.$store.$state.search_page.search_conditions || {}
      // merge params with search conditions (values)
      return { ...this.searchParams, ...conditions }
    },
    prefecturesFilter() {
      if (!this.searchConditions['filter[prefecture]']) return []
      const prefectures = []
      const slugs = this.searchConditions['filter[prefecture]'].split(',')
      slugs.forEach(slug => {
        const pref = findPrefectureBySlug(slug)
        if (pref) prefectures.push(pref.name)
      })
      return prefectures
    },
    locationCondition() {
      if (this.prefecturesFilter && this.prefecturesFilter.length === 1) {
        return this.prefecturesFilter[0].replace(/(県|都|府)$/, '')
      } else {
        return '全国'
      }
    },
    contentTypes() {
      return this.searchConditions['filter[content_type]'] ? this.searchConditions['filter[content_type]'].split(',') : []
    },
    eventTypeNames() {
      if (this.pageDetector.isActivityDetailPage || this.pageDetector.isActivityTopPage) return [this.getEventTypeName('activity')]

      const eventTypes = this.searchConditions['filter[event_type]'] ? this.searchConditions['filter[event_type]'].split(',') : []
      if (eventTypes.length === 1 && !this.pageDetector.isSearchEventsPage) return [this.getEventTypeName(eventTypes)]

      const eventTypesByContent = uniqArray(flattenArray(this.contentTypes.map(v => CONTENT_EVENT_TYPES[v]).filter(v => v)))
      if (eventTypes.length === 0) return eventTypesByContent.map(v => this.getEventTypeName(v))
      if (eventTypesByContent.length === 0) return eventTypes.map(v => this.getEventTypeName(v))

      return eventTypes.filter(v => eventTypesByContent.includes(v)).map(v => this.getEventTypeName(v))
    },
    interestNames() {
      if (this.pageDetector.isActivityDetailPage || this.pageDetector.isActivityTopPage) return []

      const interestNames = []
      const interests = this.searchConditions.interests
      const haveInterest = Number(this.searchConditions['filter[have_interest]']) === 1 || this.contentTypes.includes('have_interest')

      if (interests) interestNames.push(interests.map(v => v.name))
      if (haveInterest && interestNames.length === 0) interestNames.push('趣味コン')

      return uniqArray(flattenArray(interestNames))
    },
    selectedPrefectureName() {
      const prefecture = this.model.prefecture_slug ? findPrefectureBySlug(this.model.prefecture_slug) : null
      return prefecture ? prefecture.name : '都道府県'
    },
    genreConditions() {
      if (this.$route.query['cat_in[]']) {
        const purposes = flattenArray([this.$route.query['cat_in[]']])
        const eventTypes = EventModel.getEventTypesByPurposes(purposes)
        const eventTypeNames = eventTypes.map(eventType => this.getEventTypeName(eventType)).filter(v => v)
        if (this.searchConditions.feature_tag) eventTypeNames.push(this.searchConditions.feature_tag.name)
        return uniqArray(eventTypeNames).join(', ')
      }
      if (this.eventTypeNames.length > 0 || this.interestNames.length > 0) {
        return uniqArray([...this.eventTypeNames, ...this.interestNames]).join(', ')
      }
      if (this.searchConditions.feature_tag) {
        return this.searchConditions.feature_tag.name
      }
      return '街コン（全イベント）'
    },
    scrollDiff() {
      return this.$store.$state.window.scroll_top - this.$store.$state.window.old_scroll_top
    },
    isFirstTimeAccessKonkatsuPage() {
      const globalCookie = new GlobalCookie(this.$store)
      return globalCookie.firstTimeAccessPage && !this.$store.isLoggedIn && this.$store.isSP && this.pageDetector.isKonkatsuEventsPages
    },
    canShowHeader() {
      if (this.isFirstTimeAccessKonkatsuPage)
        return this.$store.$state.window.scroll_top > SHOW_SEARCH_HEADER_DISTANCE_ON_KONKATSU_PAGE && this.scrollDiff > 0
      if (this.pageDetector.isInterestsPage)
        return this.$store.$state.window.scroll_top > SHOW_SEARCH_HEADER_DISTANCE_ON_INTEREST_PAGE && this.scrollDiff > 0
      return this.$store.$state.window.scroll_top > SHOW_SEARCH_HEADER_DISTANCE && this.scrollDiff > 0
    },
    modeNormal() {
      return this.mode === 'normal'
    },
    modeHeader() {
      return this.mode === 'header'
    },
    searchQueryParams() {
      this.removeFeatureIfPurposeExisted()
      const formData = { ...this.model, prefectures: this.model.prefecture_slug ? [this.model.prefecture_slug] : [], features: this.model.features }
      return this.searchForm.convertFormDataToQueryParams(formData)
    },
    isSelectedPurpose() {
      const filterPurposes = this.model.purposes.filter(purpose => !['interest', 'same_hobbies'].includes(purpose))
      return filterPurposes.length > 0 || this.model.interestPurposes.length > 0 || this.model.features.includes('counseling')
    },
    isFeatureTagSelected() {
      return this.model.features && this.model.features.length > 0 && this.model.syumiplusPurposes.length === 0
    },
    dateClasses() {
      const classes = []
      if (this.model.start_date || this.model.end_date || this.model.start_dates.length > 0) classes.push('active')

      if (!this.$store.isSP) {
        let totalLength = this.selectedPrefectureName.length
        totalLength += (this.selectedAreaName || 'エリア').length
        if (totalLength <= 8) classes.push('maxWidthTotal8Chars')
        else if (totalLength <= 12) classes.push('maxWidthTotal12Chars')
        else if (totalLength <= 17) classes.push('maxWidthTotal17Chars')
        else classes.push('maxWidthTotal20Chars')
      }

      classes.push('flex-date')
      return classes
    }
  },
  watch: {
    canShowHeader() {
      if (this.modeHeader) {
        this.changeSearchToForm()
      } else {
        this.changeSearchToShow()
      }
    },
    modal(val, _oldVal) {
      if (this.$store.isSP) {
        this.isModalOpen = Boolean(val)
      } else {
        this.isModalOpen = false
      }
    }
  },
  mounted() {
    this.side = this.canShowHeader ? 'show' : 'form'
  },
  methods: {
    removeFeatureIfPurposeExisted() {
      if (
        !this.$store.isSP &&
        ((this.model.syumiplusPurposes && this.model.syumiplusPurposes.length > 0) ||
          (this.model.interestPurposes && this.model.interestPurposes.length > 0))
      )
        this.model.features = []
    },
    openPurposeModal() {
      this.modal = 'purpose'
      if (this.$store.isSP) {
        this.currentYPosition = window.pageYOffset
      }
      if (this.pageDetector.isFeaturesEventsPage) {
        this.model.features = []
      }
    },
    openModalSearch() {
      this.modalSearch = true
      this.currentYPosition = window.pageYOffset
      document.body.classList.add('openModal')
    },
    getEventTypeName(eventType) {
      if (eventType === 'other') return '友達作り'
      return this.$useI18(`event.attributes.event_type.${eventType}`)
    },
    closeModalSearch() {
      this.modalSearch = false
    },
    changeSearchToForm() {
      this.side = 'show'
    },
    changeSearchToShow() {
      this.side = 'form'
    }
  }
}
</script>

<style lang="scss" scoped>
.searchConditions {
  position: relative;
  &.headerMode {
    position: fixed;
    top: 0;
    width: 100%;
    display: flex;
    transition: 0.2s;
    transform: translateY(-100%);
    z-index: 1100;
    &.active {
      transform: translateY(0);
      .selectForm {
        box-shadow: 0 3px 6px $black-016;
      }
      .listConditions {
        background: $white;
        box-shadow: 0 3px 6px $black-016;
      }
    }
    .selectForm {
      padding: 1rem 1.6rem;
    }
  }
  &.normalMode {
    @include pc {
      background: $gray-f1;
      padding: 2rem 1rem 0.8rem;
    }
  }
  &-content {
    display: flex;
    color: $blue-pale;
    font-weight: 700;
    @include pc {
      padding: 1rem 1rem;
    }
    @include sp {
      padding: 0 1.6rem;
      width: 100%;
    }
    &.selectForm {
      display: block;
    }
  }
}

.optionItemGroup {
  display: flex;
  width: 100%;
  @include pc {
    flex-wrap: nowrap;
  }
}

.optionItem {
  display: flex;
  padding: 0.8rem;
  width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  align-items: center;
  &.btn-select-tag {
    cursor: pointer;
  }
  &:last-child {
    margin-bottom: 0;
  }
  &.condition {
    width: auto;
    margin-top: 1.2rem;
    @include pc {
      flex: 0 0 auto;
    }
    @include sp {
      &:nth-child(1),
      &:nth-child(2) {
        padding-top: 0.9rem;
      }
    }
    &.flex-date {
      @include pc {
        flex: 0 0 auto;
        min-width: 0;
        overflow: hidden;
      }
    }
    /* 都道府県名とエリア名の合計長さに基づくクラス */
    &.maxWidthTotal8Chars {
      @include pc {
        max-width: calc(100% - 31rem);
      }
    }
    &.maxWidthTotal12Chars {
      @include pc {
        max-width: calc(100% - 36rem);
      }
    }
    &.maxWidthTotal17Chars {
      @include pc {
        max-width: calc(100% - 45rem);
      }
    }
    &.maxWidthTotal20Chars {
      @include pc {
        max-width: calc(100% - 50rem);
      }
    }
    &:not(:last-child) {
      margin-right: 0.6rem;
    }
  }
  &.active {
    .optionItem-value {
      @include textEllipsis;
      color: $white;
      font-weight: 700;
    }
    .time {
      background: $cerulean-blue;
      width: 70px;
    }
    .filter {
      color: $white;
      g {
        stroke: $white;
      }
    }
  }
  &-value {
    font-size: 1.4rem;
    font-weight: 500;
    color: $black-1a;
    &.selectedDate {
      @include pc {
        width: 100%;
        max-width: 100%;
        @include textEllipsis;
      }
    }
    &.disable {
      color: $gray-c6;
    }
    &.time {
      @include pc {
        cursor: pointer;
      }
      height: 30px;
      background: $white;
      border: none;
      outline: none;
      option {
        color: $black-1a;
      }
    }
    .filter {
      cursor: pointer;
      @include pc {
        color: $black-1a;
      }
      .icon {
        position: relative;
        top: 1px;
      }
    }
    &.purpose {
      width: 90%;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &-count {
      font-weight: 400;
    }
  }
}
</style>
